'use client'

import { Rotate3DIcon } from 'lucide-react'
import Image from 'next/image'
import { useState } from 'react'
import { useLocale } from '@/components/LocaleContext'
import { ProductImageZoom } from '@/components/frontend/ProductImageZoom'
import { Product360View } from '../../../components/shared'

/** Main image + vertical thumbnail strip; optional 360° view in same slot. */
export function ProductGallery({
  imageUrl,
  imageAlt,
  thumbnailUrls,
  has360 = false,
  imageUrlsFor360 = [],
}: {
  imageUrl: string
  imageAlt: string
  thumbnailUrls: string[]
  has360?: boolean
  imageUrlsFor360?: string[]
}) {
  const { locale } = useLocale()
  const tByLocale: Record<
    string,
    {
      viewImageLabel: string
      open360AriaLabel: string
      backToPhotosLabel: string
    }
  > = {
    en: {
      viewImageLabel: 'View image',
      open360AriaLabel: 'Open 360 degree view',
      backToPhotosLabel: 'Back to photos',
    },
    fr: {
      viewImageLabel: "Voir l'image",
      open360AriaLabel: 'Ouvrir la vue a 360 degres',
      backToPhotosLabel: 'Retour aux photos',
    },
    ar: {
      viewImageLabel: 'عرض الصورة',
      open360AriaLabel: 'فتح عرض 360 درجة',
      backToPhotosLabel: 'العودة إلى الصور',
    },
  }
  const t = tByLocale[locale] ?? tByLocale.en
  const images = (thumbnailUrls.length > 0 ? thumbnailUrls : [imageUrl]).slice(0, 4)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [viewMode, setViewMode] = useState<'gallery' | '360'>('gallery')
  const mainUrl = images[selectedIndex] ?? imageUrl
  const urlsFor360 = imageUrlsFor360.length >= 2 ? imageUrlsFor360 : []

  return (
    <div className="flex gap-3 overflow-visible sm:gap-4">
      {/* Thumbnail strip - hidden in 360 mode */}
      {viewMode === 'gallery' && (
        <div className="flex flex-col gap-2 shrink-0">
          {images.map((url, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setSelectedIndex(i)}
              className={`relative w-14 h-14 sm:w-16 sm:h-16 rounded-md overflow-hidden border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-400 focus:ring-offset-2 ${
                selectedIndex === i
                  ? 'border-[#111]'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
              aria-label={`${t.viewImageLabel} ${i + 1}`}
              aria-pressed={selectedIndex === i}
            >
              <Image
                src={url}
                alt={`${imageAlt} ${i + 1}`}
                fill
                className="object-cover"
                sizes="64px"
                unoptimized={url.startsWith('http')}
              />
            </button>
          ))}
        </div>
      )}
      {/* Main area: gallery image or 360° viewer */}
      <div className="relative flex-1 min-h-0 flex flex-col gap-2 overflow-visible">
        {viewMode === 'gallery' ? (
          <ProductImageZoom
            src={mainUrl}
            alt={imageAlt}
            galleryImages={images.map((url, i) => ({
              src: url,
              alt: `${imageAlt} ${i + 1}`,
            }))}
          >
            {has360 && urlsFor360?.length >= 2 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setViewMode('360')
                }}
                className="absolute top-3 right-3 z-10 flex items-center gap-2 rounded-full bg-white/90 px-3 py-2 shadow-md backdrop-blur transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-neutral-400"
                aria-label={t.open360AriaLabel}
              >
                <span className="text-lg leading-none">
                  <Rotate3DIcon />
                </span>
                <span className="font-poppins text-xs sm:text-sm font-medium text-[#111]">
                  360°
                </span>
              </button>
            )}
          </ProductImageZoom>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setViewMode('gallery')}
              className="font-poppins text-xs text-neutral-500 hover:text-[#111] focus:outline-none focus:ring-2 focus:ring-neutral-400 focus:ring-offset-2 rounded self-start"
            >
              ← {t.backToPhotosLabel}
            </button>
            <Product360View imageUrls={urlsFor360} imageAlt={imageAlt} />
          </>
        )}
      </div>
    </div>
  )
}
